import {
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  doc,
  getDoc,
  limit,
  collection,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { subMonths, startOfMonth, endOfMonth, format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { COLLECTIONS } from '../firebase/collections';

// Normalise les statuts stockés sous différents formats en valeurs canoniques
const normalizeStatus = (status) => {
  if (!status) return null;
  if (status === 'Terminé' || status === 'completed') return 'completed';
  if (status === 'En cours' || status === 'in_progress' || status === 'inProgress') return 'inProgress';
  if (status === 'En attente' || status === 'pending') return 'pending';
  if (status === 'cancelled') return 'cancelled';
  return status;
};

// Construit la requête de base filtrée par école (ou globale pour les super_admins)
const buildSchoolQuery = (collectionName, schoolId, extraConstraints = []) => {
  const constraints = schoolId
    ? [where('schoolId', '==', schoolId), ...extraConstraints]
    : [...extraConstraints];
  return query(collection(db, collectionName), ...constraints);
};

// Statistiques générales — toutes les demandes, filtrées côté client par école si nécessaire
export const getGeneralStats = async (currentUser, userSchool) => {
  try {
    if (!currentUser) {
      return { totalRequests: 0, byStatus: {}, byPriority: {} };
    }

    const q = query(collection(db, COLLECTIONS.WORK_REQUESTS));
    const querySnapshot = await getDocs(q);

    const stats = {
      totalRequests: 0,
      byStatus: { completed: 0, inProgress: 0, pending: 0, cancelled: 0 },
      byPriority: { urgent: 0, high: 0, medium: 0, low: 0 },
    };

    querySnapshot.forEach((requestDoc) => {
      const data = requestDoc.data();

      // Filtrer côté client par école (inclut les demandes sans schoolId pour compatibilité)
      if (userSchool?.id && data.schoolId && data.schoolId !== userSchool.id) return;

      stats.totalRequests++;

      const status = normalizeStatus(data.status);
      if (status && stats.byStatus[status] !== undefined) {
        stats.byStatus[status]++;
      }

      if (data.priority && stats.byPriority[data.priority] !== undefined) {
        stats.byPriority[data.priority]++;
      }
    });

    return stats;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques générales:', error);
    return { totalRequests: 0, byStatus: {}, byPriority: {} };
  }
};

// Statistiques mensuelles — toutes les demandes, filtrées côté client
export const getMonthlyStats = async (currentUser, userSchool) => {
  try {
    if (!currentUser) {
      return { monthlyData: [], recentRequests: [] };
    }

    const q = query(collection(db, COLLECTIONS.WORK_REQUESTS));

    const querySnapshot = await getDocs(q);

    // Construire les buckets mensuels en un seul passage sur les documents
    const monthBuckets = Array.from({ length: 6 }, (_, i) => {
      const monthDate = subMonths(new Date(), 5 - i);
      return {
        month: format(monthDate, 'MMMM yyyy', { locale: fr }),
        monthStart: startOfMonth(monthDate),
        monthEnd: endOfMonth(monthDate),
        total: 0,
        byStatus: { completed: 0, inProgress: 0, pending: 0 },
      };
    });

    const allRequests = [];

    querySnapshot.forEach((requestDoc) => {
      const data = requestDoc.data();
      const createdAt = data.createdAt?.toDate();

      // Filtrer côté client par école (inclut les demandes sans schoolId pour compatibilité)
      if (userSchool?.id && data.schoolId && data.schoolId !== userSchool.id) return;

      allRequests.push({ id: requestDoc.id, ...data, createdAt });

      if (!createdAt) return;

      for (const bucket of monthBuckets) {
        if (createdAt >= bucket.monthStart && createdAt <= bucket.monthEnd) {
          bucket.total++;
          const status = normalizeStatus(data.status);
          if (status && bucket.byStatus[status] !== undefined) {
            bucket.byStatus[status]++;
          }
          break;
        }
      }
    });

    // 10 demandes les plus récentes
    allRequests.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    const topRecent = allRequests.slice(0, 10);

    // Enrichir avec les noms de services — dédoublonner les IDs avant de fetch
    const serviceIds = [...new Set(
      topRecent.map(r => r.assignedServiceId).filter(Boolean)
    )];

    const serviceNames = {};
    await Promise.all(
      serviceIds.map(async (serviceId) => {
        try {
          const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, serviceId));
          if (serviceDoc.exists()) {
            serviceNames[serviceId] = serviceDoc.data().name || 'Service technique';
          }
        } catch {
          // Service introuvable — on continue
        }
      })
    );

    const enrichedRequests = topRecent.map(request => ({
      ...request,
      assignedServiceName: serviceNames[request.assignedServiceId] || request.assignedServiceName || '',
    }));

    return {
      monthlyData: monthBuckets.map(({ monthStart, monthEnd, ...rest }) => rest),
      recentRequests: enrichedRequests,
    };
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques mensuelles:', error);
    return { monthlyData: [], recentRequests: [] };
  }
};

// Statistiques par service — filtrées par école côté serveur
export const getServiceStats = async (currentUser, userSchool) => {
  try {
    const stats = {
      byService: {},
      byServiceType: {},
      averageTimeByService: {},
    };

    const q = buildSchoolQuery(COLLECTIONS.WORK_REQUESTS, userSchool?.id);
    const requestsSnapshot = await getDocs(q);

    // Collecter les serviceIds uniques avant de faire des getDoc
    const serviceIds = new Set();
    requestsSnapshot.forEach((requestDoc) => {
      const serviceId = requestDoc.data().serviceId;
      if (serviceId) serviceIds.add(serviceId);
    });

    // Fetch tous les services en parallèle — un seul aller-retour par service unique
    const serviceData = {};
    await Promise.all(
      [...serviceIds].map(async (serviceId) => {
        try {
          const serviceDoc = await getDoc(doc(db, COLLECTIONS.SERVICES, serviceId));
          if (serviceDoc.exists()) {
            serviceData[serviceId] = serviceDoc.data();
          }
        } catch {
          // Service introuvable — on continue
        }
      })
    );

    requestsSnapshot.forEach((requestDoc) => {
      const request = requestDoc.data();
      const serviceId = request.serviceId;

      if (!serviceId || !serviceData[serviceId]) return;

      const service = serviceData[serviceId];

      stats.byService[serviceId] = stats.byService[serviceId] || {
        name: service.name,
        count: 0,
        completed: 0,
        totalTime: 0,
      };

      stats.byService[serviceId].count++;

      const serviceType = service.type || 'Non défini';
      stats.byServiceType[serviceType] = (stats.byServiceType[serviceType] || 0) + 1;

      if (request.status === 'completed' && request.completedAt && request.createdAt) {
        stats.byService[serviceId].completed++;
        stats.byService[serviceId].totalTime +=
          request.completedAt.toDate() - request.createdAt.toDate();
      }
    });

    Object.keys(stats.byService).forEach((serviceId) => {
      const service = stats.byService[serviceId];
      if (service.completed > 0) {
        stats.averageTimeByService[serviceId] =
          service.totalTime / service.completed / (1000 * 60 * 60 * 24);
      }
    });

    return stats;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques par service:', error);
    throw error;
  }
};

// Statistiques par école — filtrées par école côté serveur
export const getSchoolStats = async (currentUser, userSchool) => {
  try {
    const stats = {
      bySchool: {},
      totalSchools: 0,
      averageRequestsPerSchool: 0,
    };

    const q = buildSchoolQuery(COLLECTIONS.WORK_REQUESTS, userSchool?.id);
    const requestsSnapshot = await getDocs(q);

    // Collecter les schoolIds uniques
    const schoolIds = new Set();
    requestsSnapshot.forEach((requestDoc) => {
      const schoolId = requestDoc.data().schoolId;
      if (schoolId) schoolIds.add(schoolId);
    });

    // Fetch toutes les écoles en parallèle
    const schoolData = {};
    await Promise.all(
      [...schoolIds].map(async (schoolId) => {
        try {
          const schoolDoc = await getDoc(doc(db, COLLECTIONS.SCHOOLS, schoolId));
          if (schoolDoc.exists()) {
            schoolData[schoolId] = schoolDoc.data();
          }
        } catch {
          // École introuvable — on continue
        }
      })
    );

    requestsSnapshot.forEach((requestDoc) => {
      const request = requestDoc.data();
      const schoolId = request.schoolId;

      if (!schoolId || !schoolData[schoolId]) return;

      const school = schoolData[schoolId];

      stats.bySchool[schoolId] = stats.bySchool[schoolId] || {
        name: school.name,
        count: 0,
        completed: 0,
        open: 0,
      };

      stats.bySchool[schoolId].count++;

      if (request.status === 'completed') {
        stats.bySchool[schoolId].completed++;
      } else if (request.status !== 'cancelled') {
        stats.bySchool[schoolId].open++;
      }
    });

    stats.totalSchools = Object.keys(stats.bySchool).length;
    if (stats.totalSchools > 0) {
      stats.averageRequestsPerSchool = requestsSnapshot.size / stats.totalSchools;
    }

    return stats;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques par école:', error);
    throw error;
  }
};

// Statistiques de performance — filtrées par école côté serveur
export const getPerformanceStats = async (currentUser, userSchool) => {
  try {
    const stats = {
      averageResolutionTime: 0,
      resolutionRates: {},
      satisfactionRates: {},
      performanceByService: {},
    };

    const q = buildSchoolQuery(COLLECTIONS.WORK_REQUESTS, userSchool?.id);
    const requestsSnapshot = await getDocs(q);

    let totalResolutionTime = 0;
    let completedCount = 0;

    requestsSnapshot.forEach((requestDoc) => {
      const request = requestDoc.data();

      // Initialiser le compteur de priorité
      if (request.priority) {
        stats.resolutionRates[request.priority] = stats.resolutionRates[request.priority] || {
          total: 0,
          completed: 0,
        };
        stats.resolutionRates[request.priority].total++;
      }

      if (request.status === 'completed' && request.completedAt && request.createdAt) {
        completedCount++;
        const resolutionTime =
          request.completedAt.toDate() - request.createdAt.toDate();
        totalResolutionTime += resolutionTime;

        if (request.priority) {
          stats.resolutionRates[request.priority].completed++;
        }
      }

      if (request.satisfaction) {
        stats.satisfactionRates[request.satisfaction] =
          (stats.satisfactionRates[request.satisfaction] || 0) + 1;
      }

      if (request.serviceId) {
        stats.performanceByService[request.serviceId] =
          stats.performanceByService[request.serviceId] || {
            total: 0,
            completed: 0,
            averageResolutionTime: 0,
            totalResolutionTime: 0,
          };

        stats.performanceByService[request.serviceId].total++;

        if (request.status === 'completed' && request.completedAt && request.createdAt) {
          stats.performanceByService[request.serviceId].completed++;
          stats.performanceByService[request.serviceId].totalResolutionTime +=
            request.completedAt.toDate() - request.createdAt.toDate();
        }
      }
    });

    if (completedCount > 0) {
      stats.averageResolutionTime =
        totalResolutionTime / completedCount / (1000 * 60 * 60 * 24);
    }

    Object.keys(stats.performanceByService).forEach((serviceId) => {
      const service = stats.performanceByService[serviceId];
      if (service.completed > 0) {
        service.averageResolutionTime =
          service.totalResolutionTime / service.completed / (1000 * 60 * 60 * 24);
      }
    });

    return stats;
  } catch (error) {
    console.error('Erreur lors de la récupération des statistiques de performance:', error);
    throw error;
  }
};
